import { Component, Vue } from 'vue-property-decorator';
import { listDispatchTasks, OrderDispatchTask } from '@/api/receipt-management/order-dispatch';
import { ProductionOrderProductResource } from '@/resource/model';
import { MesSyncStatusEnum } from '@/resource/enum/mes-sync-status';
import { messageError } from '@/utils';

/** Batch task lookup for the visible page, never one network call per item. */
@Component
export class OrderDispatchMixin extends Vue {
  public dispatchTasks: OrderDispatchTask[] = [];
  public dispatchLoading = false;
  public dispatchUnavailable = false;
  private dispatchGeneration = 0;
  private dispatchOrderId?: number;
  private dispatchTimer?: ReturnType<typeof setTimeout>;

  /** 复用MES状态枚举获取翻译键；缺失或未知状态沿用“未同步”展示。 */
  public getMesSyncStatusI18Key(status?: MesSyncStatusEnum | null): string {
    const key: string =
      MesSyncStatusEnum[status ?? MesSyncStatusEnum.notSync] || MesSyncStatusEnum[MesSyncStatusEnum.notSync];
    return `mesSyncStatusEnum.${key}`;
  }

  public beforeDestroy(): void {
    this.dispatchGeneration++;
    if (this.dispatchTimer) clearTimeout(this.dispatchTimer);
  }
  public async loadDispatchTasks(rows: ProductionOrderProductResource[], orderId?: number): Promise<void> {
    const generation = ++this.dispatchGeneration;
    this.dispatchOrderId = orderId;
    if (this.dispatchTimer) clearTimeout(this.dispatchTimer);
    this.dispatchLoading = true;
    this.dispatchUnavailable = false;
    const ids = Array.from(new Set(rows.map(row => row.orderId || orderId).filter(Boolean).map(String)));
    try {
      const tasks = await listDispatchTasks(ids);
      if (generation === this.dispatchGeneration) {
        const wasActive = this.dispatchTasks.some(task => [10, 20].includes(task.status));
        this.dispatchTasks = tasks;
        if (wasActive && !tasks.some(task => [10, 20].includes(task.status))) {
          this.$emit('dispatch-settled');
        }
        if (tasks.some(task => [10, 20].includes(task.status))) {
          this.dispatchTimer = setTimeout(() => this.loadDispatchTasks(rows, orderId), 5000);
        }
      }
    } catch (error) {
      if (generation === this.dispatchGeneration) {
        this.dispatchUnavailable = true;
        messageError(error as any);
      }
    } finally {
      if (generation === this.dispatchGeneration) this.dispatchLoading = false;
    }
  }
  public dispatchTask(row: ProductionOrderProductResource, target: 10 | 20): OrderDispatchTask | undefined {
    const matching = this.dispatchTasks.find(task => task.target === target && task.itemIds.map(String).includes(String(row.id)));
    const orderId = row.orderId || this.dispatchOrderId || matching?.orderId;
    // Earlier failures block all subsequent work for the same order/target, including other items.
    // Show the blocker and its retry action rather than an endlessly waiting newer task.
    const blocker = this.dispatchTasks.slice().reverse().find(task => task.target === target &&
      String(task.orderId) === String(orderId) && task.status === 40 && task.blocking !== false);
    // 完整回执不被其他创建失败覆盖；仍展示取消进度，避免掩盖待删除的外部工单。
    if (target === 20 && row.mesSyncStatus === 20 && row.mesOrderId > 0) {
      return blocker?.operation === 30 ? blocker : matching?.operation === 30 ? matching : undefined;
    }
    return blocker || matching;
  }
  public dispatchBusy(row: ProductionOrderProductResource, target: 10 | 20): boolean {
    const task = this.dispatchTask(row, target);
    const orderId = row.orderId || this.dispatchOrderId || task?.orderId;
    const pending = this.dispatchTasks.some(candidate => candidate.target === target &&
      String(candidate.orderId) === String(orderId) && [10, 20].includes(candidate.status));
    return this.dispatchLoading || this.dispatchUnavailable || pending;
  }

  /** 手动MES批量入口由后端逐项去重和补偿；已有任务不能禁止整批提交。 */
  public get manualMesUnavailable(): boolean {
    return this.dispatchLoading || this.dispatchUnavailable;
  }
}
